import { fetchAuthSession } from "aws-amplify/auth";

export type Role = "owner" | "consultant";

export type AgencyApproval = "pending" | "approved" | "rejected";

export type BillingAccess = { state: "full" } | { state: "locked"; reason: "no_subscription" | "payment_failed" | "ended" };

export interface Me {
  user: { id: string; email: string; displayName: string | null; role: Role; platformAdmin: boolean };
  tenant: {
    id: string;
    name: string;
    approval: AgencyApproval;
    billing: { enabled: false } | { enabled: true; plan: "paid" | "free"; access: BillingAccess };
  };
}

export type BillingInterval = "month" | "year";
export interface PlanPrice {
  interval: BillingInterval;
  currency: string;
  /** Pence: the agency's first person, then each other active user. */
  first: number;
  additional: number;
  /** Pence: what this agency would pay now. */
  total: number;
}
export type BillingOverview =
  | { enabled: false }
  | {
      enabled: true;
      plan: "paid" | "free";
      planNote: string | null;
      status: "none" | "incomplete" | "active" | "past_due" | "unpaid" | "canceled" | "paused";
      access: BillingAccess;
      interval: BillingInterval | null;
      periodEnd: string | null;
      cancelAtPeriodEnd: boolean;
      pastDueSince: string | null;
      graceEndsAt: string | null;
      seats: number;
      prices: Record<BillingInterval, PlanPrice>;
    };

/** A new agency as platform admins see it on the Approvals page. */
export interface AgencyForApproval {
  tenantId: string;
  name: string;
  createdAt: string;
  ownerEmail: string | null;
  approval: AgencyApproval;
  decidedAt: string | null;
  decidedBy: string | null;
  billingPlan: "paid" | "free";
  billingPlanNote: string | null;
  subscriptionStatus: string;
  seats: number;
}

export interface TeamUser {
  id: string;
  email: string;
  displayName: string | null;
  role: Role;
  status: "active" | "deactivated";
  createdAt: string;
}

export interface Invitation {
  id: string;
  email: string;
  role: Role;
  status: "pending" | "accepted" | "revoked";
  expiresAt: string;
  createdAt: string;
}

export interface InvitationPreview {
  companyName: string;
  email: string;
  role: Role;
}

export type ClientStatus = "lead" | "client";

export interface Company {
  id: string;
  name: string;
  website: string | null;
  websiteDomain: string | null;
  location: string | null;
  industry: string | null;
  locked: boolean;
  createdByYourTeam: boolean;
  canEdit: boolean;
  canLock: boolean;
  updatedAt: string;
  client: { status: ClientStatus; notes: string; createdAt: string; updatedAt: string } | null;
}

/** A company on the agency's list, with its open jobs and when activity was last logged. */
export type ClientListEntry = Company & { openJobs: number; lastActivity: string | null };

export interface CompanyFields {
  name: string;
  website: string | null;
  location: string | null;
  industry: string | null;
}

export interface HistoryEntry {
  revision: number;
  kind: "create" | "update" | "revert" | "lock" | "unlock";
  data: CompanyFields & { locked: boolean };
  createdAt: string;
  byYourTeam: boolean;
  changedBy: string | null;
}

export interface Contact {
  id: string;
  companyId: string;
  fullName: string;
  jobTitle: string | null;
  email: string | null;
  phone: string | null;
  linkedinUrl: string | null;
  notes: string;
  archived: boolean;
}

export type ContactFields = Pick<Contact, "fullName" | "jobTitle" | "email" | "phone" | "linkedinUrl">;

export type ActivityKind = "call" | "email" | "meeting" | "note";

export interface Activity {
  id: string;
  kind: ActivityKind;
  summary: string;
  occurredAt: string;
  contact: { id: string; fullName: string } | null;
  createdBy: string | null;
}

export interface Reminder {
  id: string;
  dueOn: string;
  note: string;
  done: boolean;
  company: { id: string; name: string };
  contact: { id: string; fullName: string } | null;
  assignedTo: { id: string; email: string; name: string };
}

export interface TodayList {
  today: string;
  scope: "mine" | "team";
  overdue: Reminder[];
  dueToday: Reminder[];
  upcoming: Reminder[];
}

export interface StoredFile {
  id: string;
  filename: string;
  contentType: string;
  sizeBytes: number;
  status: "pending" | "uploaded";
}

export type Currency = "GBP" | "EUR" | "USD";
export interface RebateBand {
  fromWeek: number;
  toWeek: number;
  refundPercent: number;
}
export type Rebate =
  | { kind: "none" }
  | { kind: "free_replacement"; periodWeeks: number }
  | { kind: "sliding_refund"; periodWeeks: number; schedule: RebateBand[] };

export interface Terms {
  id: string;
  feeType: "percent" | "fixed";
  feePercent: number | null;
  feeFixed: number | null;
  currency: Currency;
  paymentTermsDays: number;
  rebate: Rebate;
  agreedOn: string;
  document: { id: string; filename: string } | null;
  notes: string;
  createdBy: string | null;
  createdAt: string;
}

export type TermsInput = Omit<Terms, "id" | "document" | "createdBy" | "createdAt" | "feePercent" | "feeFixed"> & {
  feePercent?: number;
  feeFixed?: number;
  documentFileId?: string | null;
};

export type JobStatus = "open" | "on_hold" | "filled" | "lost";
export type JobFee = { type: "percent"; percent: number } | { type: "fixed"; amount: number };

export interface Job {
  id: string;
  title: string;
  company: { id: string; name: string };
  location: string | null;
  skills: string[];
  salaryMin: number | null;
  salaryMax: number | null;
  currency: Currency;
  fee: JobFee;
  feeFromTerms: boolean;
  status: JobStatus;
  hiringContact: { id: string; fullName: string } | null;
  owner: { id: string; email: string; name: string };
  notes: string;
  description: string;
  roleTerms: RoleTerms;
  companyIndustry: string | null;
  openedOn: string;
  stageCount: number;
  inProcess: number;
  placed: number;
}

/** What the candidate must be told about the role (Conduct Regulations 2003, regs 18 and 21). */
export type RoleTermKey = "clientBusiness" | "startDate" | "duration" | "hours" | "benefits" | "payIntervals" | "noticePeriod" | "qualifications" | "healthSafety" | "expenses";
export type RoleTerms = Partial<Record<RoleTermKey, string>>;

export interface Stage {
  id: string;
  position: number;
  name: string;
  notes: string;
}

export interface JobInput {
  title: string;
  location: string | null;
  skills?: string[];
  salaryMin: number | null;
  salaryMax: number | null;
  currency?: Currency;
  fee?: JobFee;
  hiringContactId: string | null;
  ownerId?: string;
  notes?: string;
  description?: string;
  roleTerms?: RoleTerms;
}

/** A search result: where the query matched in the candidate's notes or CV, highlighted, if it did. */
export type CandidateSummary = Omit<Candidate, "timeline">;
export type CandidateHit = CandidateSummary & { snippet: { text: string; hit: boolean }[] | null };

/** A talent pool: a named list of candidates the agency keeps warm. */
export interface Pool {
  id: string;
  name: string;
  description: string;
  size: number;
  createdBy: string | null;
  createdAt: string;
  updatedAt: string;
}
export type PoolMember = CandidateSummary & { pool: { note: string; addedAt: string; addedBy: string | null } };
export interface PoolMembership {
  poolId: string;
  name: string;
  note: string;
  addedAt: string;
}

export interface Interview {
  id: string;
  applicationId: string;
  stageName: string;
  startsAt: string;
  durationMinutes: number;
  format: "video" | "phone" | "in_person";
  location: string;
  interviewers: string;
  notes: string;
  status: "scheduled" | "cancelled";
  sequence: number;
  updatedAt: string;
}

export interface InviteContext {
  agency: string;
  job: { title: string };
  company: { name: string };
  candidate: { fullName: string; email: string | null };
  contact: { fullName: string; email: string | null } | null;
  recruiter: { name: string; email: string };
}

export type InterviewInput = Pick<Interview, "startsAt" | "durationMinutes" | "format" | "location" | "interviewers" | "notes">;

export type ShortlistDecision = "interview" | "maybe" | "no";

/** A shortlist sent to a client, as the recruiter sees it. */
export interface Shortlist {
  id: string;
  contact: { id: string; fullName: string } | null;
  message: string;
  createdBy: string;
  createdAt: string;
  expiresAt: string;
  revoked: boolean;
  firstViewedAt: string | null;
  lastViewedAt: string | null;
  candidates: { id: string; applicationId: string; candidateName: string; decision: ShortlistDecision | null; feedback: string; decidedAt: string | null }[];
}

export interface NewShortlist {
  contactId: string | null;
  message: string;
  expiresInDays: 7 | 30 | 90;
  candidates: { applicationId: string; pitch: string; includeCv: boolean; consentConfirmed: true }[];
}

/** What the client sees on the shortlist link. */
export interface ClientShortlist {
  agency: string;
  job: { title: string; location: string | null; description: string };
  company: string;
  contactName: string | null;
  message: string;
  recruiter: { name: string; email: string };
  expiresAt: string;
  candidates: {
    id: string;
    fullName: string;
    currentTitle: string | null;
    location: string | null;
    noticePeriod: string | null;
    salaryExpectation: number | null;
    currency: Currency;
    skills: string[];
    pitch: string;
    hasCv: boolean;
    decision: ShortlistDecision | null;
    feedback: string;
  }[];
}

/** The caller's email logging address, the addresses they send from, and what happened to recent emails. */
export interface EmailLog {
  enabled: boolean;
  address: string | null;
  senders: string[];
  loginEmail: string;
  messages: {
    id: string;
    receivedAt: string;
    status: "logged" | "unmatched" | "rejected" | "discarded";
    reason: string;
    from: string | null;
    subject: string | null;
    candidates: number;
    contacts: number;
  }[];
}

/** A browser extension connection. The code itself is only returned once, when it's created. */
export interface ExtensionToken {
  id: string;
  label: string;
  createdAt: string;
  lastUsedAt: string | null;
}

/** A candidate suggested for a job, with what matched. */
export type JobMatch = CandidateSummary & {
  match: { skills: string[]; skillsInText: string[]; missing: string[]; title: boolean; nearby: boolean };
};
/** Matches for a job, plus the role and town they were matched on (the web search starts from these). */
export interface JobMatches {
  role: string;
  place: string | null;
  candidates: JobMatch[];
}

/**
 * One entry in a candidate's timeline: a note they logged, or a comment left on one of the
 * candidate's applications. Neither can be edited or deleted.
 */
export type TimelineEntry =
  | { source: "note"; id: string; kind: ActivityKind; body: string; createdAt: string; author: { id: string; name: string } | null }
  | {
      source: "pipeline";
      id: string;
      kind: ApplicationEvent["kind"];
      body: string;
      createdAt: string;
      author: { id: string; name: string } | null;
      fromStage: string | null;
      toStage: string | null;
      application: { id: string; jobTitle: string; companyName: string };
    };

/** A candidate's full record. Search results leave out `notes`. */
export interface Candidate {
  id: string;
  fullName: string;
  email: string | null;
  phone: string | null;
  location: string | null;
  currentTitle: string | null;
  currentEmployer: string | null;
  salaryExpectation: number | null;
  currency: Currency;
  noticePeriod: string | null;
  linkedinUrl: string | null;
  source: string | null;
  skills: string[];
  status: "active" | "inactive";
  timeline: TimelineEntry[];
  cv: { id: string; filename: string } | null;
  owner: { id: string; email: string; name: string };
  lawfulBasis: "legitimate_interests" | "consent";
  privacyNoticeSentOn: string | null;
  erased: boolean;
  jobs: { active: number; placed: number };
  lastContact: string | null;
  updatedAt: string;
}

export type CandidateInput = Partial<
  Pick<
    Candidate,
    | "fullName"
    | "email"
    | "phone"
    | "location"
    | "currentTitle"
    | "currentEmployer"
    | "salaryExpectation"
    | "currency"
    | "noticePeriod"
    | "linkedinUrl"
    | "source"
    | "skills"
    | "status"
    | "lawfulBasis"
    | "privacyNoticeSentOn"
  >
> & { ownerId?: string };

/** Creating a candidate can include a first note. */
export type NewCandidateInput = CandidateInput & { fullName: string; notes?: string };

export type ApplicationStatus = "active" | "rejected" | "withdrawn" | "placed";

export interface PipelineCard {
  id: string;
  candidate: { id: string; fullName: string; currentTitle: string | null; currentEmployer: string | null; hasCv: boolean; erased: boolean };
  stageId: string | null;
  status: ApplicationStatus;
  stageEnteredAt: string;
  updatedAt: string;
  nextInterview: string | null;
}

export interface Placement {
  id: string;
  startDate: string;
  salary: number;
  currency: Currency;
  fee: { type: "percent"; percent: number } | { type: "fixed" };
  feeAmount: number;
  createdAt: string;
}

export interface Board {
  stages: { id: string; name: string; position: number }[];
  cards: PipelineCard[];
  placements: Record<string, Placement>;
}

export interface ApplicationEvent {
  id: string;
  kind: "added" | "moved" | "feedback" | "rejected" | "withdrawn" | "reopened" | "placed" | "unplaced" | "client_feedback" | "interview_booked" | "interview_moved" | "interview_cancelled";
  fromStage: string | null;
  toStage: string | null;
  note: string;
  by: string | null;
  at: string;
}

export interface ApplicationDetail {
  application: {
    id: string;
    jobId: string;
    jobTitle: string;
    companyName: string;
    candidateId: string;
    candidateName: string;
    candidateErased: boolean;
    stageId: string | null;
    stageName: string;
    status: ApplicationStatus;
  };
  events: ApplicationEvent[];
  placement: Placement | null;
}

export type ApplicationAction =
  | { kind: "move"; stageId: string | null; note?: string }
  | { kind: "feedback"; note: string }
  | { kind: "reject" | "withdraw" | "reopen" | "unplace"; note?: string }
  | { kind: "place"; startDate: string; salary: number; markJobFilled: boolean; note?: string };

export interface CandidateApplication {
  id: string;
  status: ApplicationStatus;
  stageName: string;
  updatedAt: string;
  job: { id: string; title: string; status: JobStatus };
  company: { id: string; name: string };
}

export type MoneyByCurrency = Partial<Record<Currency, number>>;

export interface Dashboard {
  myJobs: { id: string; title: string; status: JobStatus; company: { id: string; name: string }; inProcess: number; placed: number; lastActivity: string }[];
  stale: { applicationId: string; candidateName: string; stageName: string; days: number; job: { id: string; title: string } }[];
  /** Interviews on your jobs in the next 7 days. */
  interviews: {
    id: string;
    startsAt: string;
    durationMinutes: number;
    format: Interview["format"];
    stageName: string;
    applicationId: string;
    candidateName: string;
    job: { id: string; title: string };
    companyName: string;
  }[];
  /** Your candidates with no privacy notice recorded, oldest first (at most 20 listed). */
  privacyNotices: { total: number; overdue: number; candidates: { id: string; fullName: string; email: string | null; source: string | null; dueOn: string }[] };
  team: {
    id: string;
    email: string;
    name: string;
    role: Role;
    isYou: boolean;
    openJobs: number;
    inProcess: number;
    remindersDue: number;
    placementsThisMonth: number;
    feesThisMonth: MoneyByCurrency;
  }[];
  recentPlacements: {
    id: string;
    applicationId: string;
    candidateName: string;
    job: { id: string; title: string };
    companyName: string;
    owner: string;
    startDate: string;
    fee: { currency: Currency; amount: number };
  }[];
  agencyFeesThisMonth: MoneyByCurrency;
}

export interface PrivacySettings {
  retentionMonths: number;
  noticeContact: string | null;
  icoNumber: string | null;
  legalAcceptedVersion: string | null;
  legalAcceptedAt: string | null;
  currentLegalVersion: string;
  legalUpToDate: boolean;
}

/** What an agency's public candidate privacy notice says about it. */
export interface PublicNotice {
  agency: string;
  contact: string | null;
  icoNumber: string | null;
  retentionMonths: number;
}

/** A person's private calendar address. The token comes back only when it's made. */
export interface CalendarFeed {
  createdAt: string;
  lastFetchedAt: string | null;
}

export interface Report {
  from: string;
  to: string;
  totals: { placements: number; fees: MoneyByCurrency; averageDaysToFill: number | null };
  byMonth: { month: string; placements: number; fees: MoneyByCurrency }[];
  byConsultant: {
    id: string;
    name: string;
    placements: number;
    fees: MoneyByCurrency;
    jobsOpened: number;
    candidatesAdded: number;
    putForward: number;
    interviewsBooked: number;
    clientActivities: number;
    candidateNotes: number;
  }[];
  byClient: { id: string; name: string; placements: number; fees: MoneyByCurrency }[];
  bySource: { source: string; added: number; placed: number }[];
  funnel: { putForward: number; interviewed: number; offered: number; placed: number; rejected: number; withdrawn: number; active: number };
}

export class ApiError extends Error {
  constructor(
    readonly status: number,
    message: string,
    readonly code?: string,
    readonly details: Record<string, unknown> = {},
  ) {
    super(message);
  }
}

let apiBase = "/api";
export function setApiBase(base: string) {
  apiBase = base.replace(/\/$/, "");
}

/**
 * Calls the API with the Cognito ID token. The API needs the ID token (not the access token)
 * because sign-up and invitation acceptance rely on its verified email claim.
 */
/** The full address of an API path, for links the browser follows itself (downloads). */
export const apiUrl = (path: string) => `${apiBase}${path}`;

async function request<T>(method: string, path: string, body?: unknown, auth = true): Promise<T> {
  const headers: Record<string, string> = {};
  if (body !== undefined) headers["content-type"] = "application/json";
  if (auth) {
    const session = await fetchAuthSession();
    const token = session.tokens?.idToken?.toString();
    if (!token) throw new ApiError(401, "You are signed out.");
    headers.authorization = `Bearer ${token}`;
  }
  const res = await fetch(`${apiBase}${path}`, {
    method,
    headers,
    body: body === undefined ? undefined : JSON.stringify(body),
  });
  if (res.status === 204) return undefined as T;
  const data = (await res.json().catch(() => ({}))) as { error?: string; code?: string } & Record<string, unknown>;
  if (!res.ok) throw new ApiError(res.status, data.error ?? `Request failed (${res.status})`, data.code, data);
  return data as T;
}

export const api = {
  me: () => request<Me>("GET", "/me"),
  agencies: (approval?: AgencyApproval) =>
    request<{ agencies: AgencyForApproval[] }>("GET", `/admin/agencies${approval ? `?approval=${approval}` : ""}`),
  decideAgency: (tenantId: string, action: "approve" | "reject") =>
    request<{ ok: true }>("POST", `/admin/agencies/${tenantId}/${action}`),
  setAgencyPlan: (tenantId: string, plan: "paid" | "free", note?: string) =>
    request<{ ok: true }>("POST", `/admin/agencies/${tenantId}/plan`, { plan, note }),
  billing: () => request<BillingOverview>("GET", "/billing"),
  checkout: (interval: BillingInterval) => request<{ url: string }>("POST", "/billing/checkout", { interval }),
  billingPortal: () => request<{ url: string }>("POST", "/billing/portal"),
  signup: (companyName: string, displayName: string, acceptLegalVersion: string) =>
    request<Me>("POST", "/signup", { companyName, displayName, acceptLegalVersion }),
  previewInvitation: (token: string) =>
    request<InvitationPreview>("GET", `/invitations/preview?token=${encodeURIComponent(token)}`, undefined, false),
  acceptInvitation: (token: string, displayName: string) => request<Me>("POST", "/invitations/accept", { token, displayName }),
  updateMe: (displayName: string) => request<{ user: TeamUser }>("PATCH", "/me", { displayName }),
  users: () => request<{ users: TeamUser[] }>("GET", "/users"),
  updateUser: (id: string, change: { role?: Role; status?: TeamUser["status"] }) =>
    request<{ user: TeamUser }>("PATCH", `/users/${id}`, change),
  invitations: () => request<{ invitations: Invitation[] }>("GET", "/invitations"),
  invite: (email: string, role: Role) =>
    request<{ invitation: Invitation; token: string }>("POST", "/invitations", { email, role }),
  revokeInvitation: (id: string) => request<void>("DELETE", `/invitations/${id}`),

  searchCompanies: (q: string) => request<{ results: Company[] }>("GET", `/companies?q=${encodeURIComponent(q)}`),
  createCompany: (fields: CompanyFields & { status: ClientStatus }) =>
    request<{ company: Company }>("POST", "/companies", fields),
  company: (id: string) => request<{ company: Company }>("GET", `/companies/${id}`),
  updateCompany: (id: string, change: Partial<CompanyFields> & { locked?: boolean }) =>
    request<{ company: Company }>("PATCH", `/companies/${id}`, change),
  companyHistory: (id: string) => request<{ history: HistoryEntry[] }>("GET", `/companies/${id}/history`),
  revertCompany: (id: string, revision: number) =>
    request<{ company: Company }>("POST", `/companies/${id}/revert`, { revision }),
  saveClient: (companyId: string, change: { status?: ClientStatus; notes?: string }) =>
    request<{ company: Company }>("PUT", `/companies/${companyId}/client`, change),
  removeClient: (companyId: string) => request<void>("DELETE", `/companies/${companyId}/client`),
  contacts: (companyId: string) => request<{ contacts: Contact[] }>("GET", `/companies/${companyId}/contacts`),
  addContact: (companyId: string, fields: ContactFields) =>
    request<{ contact: Contact }>("POST", `/companies/${companyId}/contacts`, fields),
  updateContact: (id: string, change: Partial<ContactFields> & { archived?: boolean; notes?: string }) =>
    request<{ contact: Contact }>("PATCH", `/contacts/${id}`, change),
  eraseContact: (id: string) => request<void>("DELETE", `/contacts/${id}`),
  activity: (companyId: string) =>
    request<{ activity: Activity[]; reminders: Reminder[] }>("GET", `/companies/${companyId}/activity`),
  logActivity: (
    companyId: string,
    entry: { kind: ActivityKind; summary: string; contactId?: string | null; followUp?: { dueOn: string } },
  ) => request<{ activity: Activity; reminder: Reminder | null }>("POST", `/companies/${companyId}/activity`, entry),
  updateReminder: (id: string, change: { done?: boolean; dueOn?: string }) =>
    request<{ reminder: Reminder }>("PATCH", `/reminders/${id}`, change),
  today: (scope: "mine" | "team") => request<TodayList>("GET", `/today?scope=${scope}`),
  createUpload: (file: { filename: string; contentType: string; sizeBytes: number }) =>
    request<{ file: StoredFile; upload: { url: string; fields: Record<string, string> } }>("POST", "/files", file),
  completeUpload: (id: string) => request<{ file: StoredFile }>("POST", `/files/${id}/complete`),
  downloadUrl: (id: string) => request<{ url: string }>("GET", `/files/${id}/download`),
  terms: (companyId: string) => request<{ current: Terms | null; history: Terms[] }>("GET", `/companies/${companyId}/terms`),
  recordTerms: (companyId: string, terms: TermsInput) =>
    request<{ terms: Terms }>("POST", `/companies/${companyId}/terms`, terms),
  jobs: (filter: { status?: JobStatus; owner?: "me" | "all"; companyId?: string } = {}) => {
    const q = new URLSearchParams(Object.entries(filter).filter(([, v]) => v) as [string, string][]);
    return request<{ jobs: Job[] }>("GET", `/jobs${q.size ? `?${q}` : ""}`);
  },
  job: (id: string) => request<{ job: Job; stages: Stage[] }>("GET", `/jobs/${id}`),
  createJob: (companyId: string, job: JobInput) =>
    request<{ job: Job; stages: Stage[] }>("POST", `/companies/${companyId}/jobs`, job),
  updateJob: (id: string, change: Partial<JobInput> & { status?: JobStatus }) =>
    request<{ job: Job; stages: Stage[] }>("PATCH", `/jobs/${id}`, change),
  deleteJob: (id: string) => request<void>("DELETE", `/jobs/${id}`),
  saveStages: (id: string, stages: { id?: string; name: string; notes?: string }[]) =>
    request<{ stages: Stage[] }>("PUT", `/jobs/${id}/stages`, stages),
  candidates: (filter: { q?: string; skills?: string[]; location?: string; status?: "active" | "inactive" | "all" }) => {
    const q = new URLSearchParams();
    if (filter.q) q.set("q", filter.q);
    if (filter.skills?.length) q.set("skills", filter.skills.join(","));
    if (filter.location) q.set("location", filter.location);
    if (filter.status) q.set("status", filter.status);
    return request<{ candidates: CandidateHit[] }>("GET", `/candidates${q.size ? `?${q}` : ""}`);
  },
  candidateSkills: () => request<{ skills: { skill: string; count: number }[] }>("GET", "/candidates/skills"),
  candidate: (id: string) => request<{ candidate: Candidate }>("GET", `/candidates/${id}`),
  createCandidate: (input: NewCandidateInput) =>
    request<{ candidate: Candidate }>("POST", "/candidates", input),
  updateCandidate: (id: string, change: CandidateInput) => request<{ candidate: Candidate }>("PATCH", `/candidates/${id}`, change),
  addCandidateNote: (id: string, kind: ActivityKind, body: string) =>
    request<{ candidate: Candidate }>("POST", `/candidates/${id}/notes`, { kind, body }),
  setCandidateCv: (id: string, fileId: string) => request<{ candidate: Candidate }>("PUT", `/candidates/${id}/cv`, { fileId }),
  pipeline: (jobId: string) => request<Board>("GET", `/jobs/${jobId}/pipeline`),
  extensionTokens: () => request<{ tokens: ExtensionToken[] }>("GET", "/extension/tokens"),
  createExtensionToken: (label: string) => request<{ token: ExtensionToken & { token: string } }>("POST", "/extension/tokens", { label }),
  revokeExtensionToken: (id: string) => request<void>("DELETE", `/extension/tokens/${id}`),
  skillsFromText: (text: string) => request<{ skills: string[] }>("POST", "/jobs/skills-from-text", { text }),
  interviews: (applicationId: string) => request<{ interviews: Interview[]; invite: InviteContext }>("GET", `/applications/${applicationId}/interviews`),
  bookInterview: (applicationId: string, input: InterviewInput) => request<{ interview: Interview }>("POST", `/applications/${applicationId}/interviews`, input),
  changeInterview: (id: string, change: Partial<InterviewInput>) => request<{ interview: Interview }>("PATCH", `/interviews/${id}`, change),
  cancelInterview: (id: string) => request<{ interview: Interview }>("POST", `/interviews/${id}/cancel`),
  shortlists: (jobId: string) => request<{ shortlists: Shortlist[] }>("GET", `/jobs/${jobId}/shortlists`),
  createShortlist: (jobId: string, input: NewShortlist) => request<{ shortlist: Shortlist; token: string }>("POST", `/jobs/${jobId}/shortlists`, input),
  revokeShortlist: (id: string) => request<{ ok: true }>("POST", `/shortlists/${id}/revoke`),
  newShortlistLink: (id: string) => request<{ token: string }>("POST", `/shortlists/${id}/link`),
  clientShortlist: (token: string) => request<ClientShortlist>("GET", `/client/shortlist?token=${encodeURIComponent(token)}`, undefined, false),
  clientDecide: (token: string, itemId: string, decision: ShortlistDecision, feedback: string) =>
    request<{ ok: true }>("POST", `/client/shortlist/decision?token=${encodeURIComponent(token)}`, { itemId, decision, feedback }, false),
  emailLog: () => request<EmailLog>("GET", "/email-log"),
  newEmailLogAddress: () => request<EmailLog>("POST", "/email-log/address"),
  setEmailLogSenders: (senders: string[]) => request<EmailLog>("PUT", "/email-log/senders", { senders }),
  discardLoggedEmail: (id: string) => request<{ ok: true }>("POST", `/email-log/messages/${id}/discard`),
  jobMatches: (jobId: string) => request<JobMatches>("GET", `/jobs/${jobId}/matches`),
  addToJob: (jobId: string, candidateId: string) =>
    request<ApplicationDetail>("POST", `/jobs/${jobId}/applications`, { candidateId }),
  application: (id: string) => request<ApplicationDetail>("GET", `/applications/${id}`),
  applicationAction: (id: string, action: ApplicationAction) =>
    request<ApplicationDetail>("POST", `/applications/${id}/actions`, action),
  pools: () => request<{ pools: Pool[] }>("GET", "/pools"),
  createPool: (name: string, description = "") => request<{ pool: Pool }>("POST", "/pools", { name, description }),
  pool: (id: string) => request<{ pool: Pool; members: PoolMember[] }>("GET", `/pools/${id}`),
  updatePool: (id: string, change: { name?: string; description?: string }) => request<{ pool: Pool }>("PATCH", `/pools/${id}`, change),
  deletePool: (id: string) => request<void>("DELETE", `/pools/${id}`),
  addToPool: (id: string, candidateIds: string[], note = "") =>
    request<{ added: number; alreadyIn: number; skipped: number }>("POST", `/pools/${id}/members`, { candidateIds, note }),
  setPoolNote: (id: string, candidateId: string, note: string) => request<{ ok: true }>("PATCH", `/pools/${id}/members/${candidateId}`, { note }),
  removeFromPool: (id: string, candidateId: string) => request<void>("DELETE", `/pools/${id}/members/${candidateId}`),
  poolToJob: (id: string, jobId: string) =>
    request<{ added: number; alreadyOnJob: number; inactive: number }>("POST", `/pools/${id}/add-to-job`, { jobId }),
  candidatePools: (candidateId: string) => request<{ pools: PoolMembership[] }>("GET", `/candidates/${candidateId}/pools`),
  candidateApplications: (candidateId: string) =>
    request<{ applications: CandidateApplication[] }>("GET", `/candidates/${candidateId}/applications`),
  importRows: (kind: "candidates" | "companies", rows: Record<string, string | number | null>[], dryRun: boolean) =>
    request<{
      dryRun: boolean;
      summary: { total: number; created: number; linked: number; duplicate: number; invalid: number };
      results: { index: number; status: "created" | "duplicate" | "invalid" | "linked"; errors?: string[]; existingId?: string; id?: string }[];
    }>("POST", `/import/${kind}`, { rows, dryRun }),
  dashboard: () => request<Dashboard>("GET", "/dashboard"),
  exportCandidate: (id: string) => request<Record<string, unknown>>("GET", `/candidates/${id}/export`),
  eraseCandidate: (id: string) => request<{ erased: boolean; filesDeleted: number; filesPending: number }>("DELETE", `/candidates/${id}`),
  privacy: () => request<PrivacySettings>("GET", "/privacy"),
  updatePrivacy: (change: { retentionMonths?: number; noticeContact?: string; icoNumber?: string }) => request<PrivacySettings>("PATCH", "/privacy", change),
  report: (from: string, to: string) => request<Report>("GET", `/reports?from=${from}&to=${to}`),
  calendarFeed: () => request<{ feed: CalendarFeed | null }>("GET", "/calendar-feed"),
  createCalendarFeed: () => request<{ feed: CalendarFeed & { token: string } }>("POST", "/calendar-feed"),
  revokeCalendarFeed: () => request<void>("DELETE", "/calendar-feed"),
  publicNotice: (tenantId: string) => request<PublicNotice>("GET", `/public/notice/${encodeURIComponent(tenantId)}`, undefined, false),
  acceptLegal: (version: string) => request<PrivacySettings>("POST", "/privacy/accept", { version }),
  retentionDue: () =>
    request<{ candidates: { id: string; fullName: string; status: string; lastActivity: string }[] }>("GET", "/privacy/retention"),
  keepCandidate: (id: string) => request<void>("POST", `/privacy/retention/${id}/keep`),
  clients: (status?: ClientStatus) =>
    request<{ clients: ClientListEntry[] }>("GET", `/clients${status ? `?status=${status}` : ""}`),
};

export function inviteLink(token: string): string {
  return `${window.location.origin}/join?token=${encodeURIComponent(token)}`;
}
